import { computed } from "vue";
import { toValue } from "@vueuse/core/index";
import { useRoute } from "vue-router";
import { useWindowSize } from "@vueuse/core";
import { useUserProperty } from "@/composables/useUserProperty";
import { useUserFlag } from "@/composables/useUserFlag";
import { useOnboardingFlag } from "@/composables/useOnboardingFlag";
import { useNewOnboardingFlag } from "@/composables/useNewOnboardingFlag";
import { useEncryptionGate } from "@/composables/useEncryptionGate";
import { useDisplay } from "@/composables/useDisplay";
import { useBasicMode } from "@/composables/useBasicMode";
import {
  DATA_DELETE_REQUESTED,
  HAS_EXITED_DELETE_FLOW,
  HAS_ONBOARDED_V4,
  HAS_SEEN_DD_POST_PAYMENT_ONBOARDING,
  SHOW_DD_POST_PAYMENT_ONBOARDING,
} from "@/scripts/userFlags";
import { usePostHogFeatureFlag } from "@/composables/usePostHogFeatureFlag.js";

export const SCREEN = {
  APP: "SCREEN_APP",
  DOWNLOAD_MOBILE_APP: "SCREEN_DOWNLOAD_MOBILE_APP",
  DATA_DELETE_ENROLLMENT: "SCREEN_DATA_DELETE_ENROLLMENT",
  NEW_ONBOARDING: "SCREEN_NEW_ONBOARDING",
  DEFAULT_ONBOARDING: "SCREEN_DEFAULT_ONBOARDING",
};

const { isBasicModeEnabled, isBasicModeAccessible } = useBasicMode();

export const useScreen = () => {
  const route = useRoute();
  const { width } = useWindowSize();
  const { isEncryptionAvailable } = useEncryptionGate();
  const { isMobile } = useDisplay();

  // eslint-disable-next-line no-unused-vars
  const [_0, hasLoadedUserProperties] = useUserProperty(null);
  // eslint-disable-next-line no-unused-vars
  const [_1, hasLoadedUserFlags] = useUserFlag(null);
  // eslint-disable-next-line no-unused-vars
  const [_2, hasLoadedOnboardingFlags] = useOnboardingFlag(null);
  // eslint-disable-next-line no-unused-vars
  const [_3, hasLoadedNewOnboardingFlags] = useNewOnboardingFlag(null);
  const { hasLoadedFeatureFlag: hasLoadedPostHogFlags } = usePostHogFeatureFlag(
    "identity-monitoring-enabled"
  );

  const isLoaded = computed(
    () =>
      toValue(hasLoadedUserProperties) &&
      toValue(hasLoadedUserFlags) &&
      toValue(hasLoadedOnboardingFlags) &&
      toValue(hasLoadedNewOnboardingFlags) &&
      toValue(hasLoadedPostHogFlags)
  );

  const [hasRequestedEnrollment] = useOnboardingFlag(DATA_DELETE_REQUESTED);
  const [hasExitedEnrollment] = useOnboardingFlag(HAS_EXITED_DELETE_FLOW);

  const [hasNewOnboarding] = useUserFlag(SHOW_DD_POST_PAYMENT_ONBOARDING);
  const [hasExitedNewOnboarding] = useNewOnboardingFlag(
    HAS_SEEN_DD_POST_PAYMENT_ONBOARDING
  );

  const [hasOnboarding] = useUserFlag("show_onboarding_v4");
  const [hasExitedOnboarding] = useOnboardingFlag(HAS_ONBOARDED_V4);

  const screen = computed(() => {
    if (!toValue(isLoaded)) {
      return SCREEN.APP;
    }

    if (toValue(hasRequestedEnrollment) && !toValue(hasExitedEnrollment)) {
      return SCREEN.DATA_DELETE_ENROLLMENT;
    }

    if (
      toValue(hasNewOnboarding) &&
      !toValue(hasExitedNewOnboarding) &&
      (toValue(isBasicModeAccessible) ? !toValue(isBasicModeEnabled) : true) &&
      toValue(isEncryptionAvailable)
    ) {
      return SCREEN.NEW_ONBOARDING;
    }

    if (
      toValue(hasOnboarding) &&
      !toValue(hasNewOnboarding) &&
      !toValue(hasExitedOnboarding) &&
      !toValue(hasExitedNewOnboarding) &&
      !toValue(isBasicModeEnabled) &&
      toValue(isEncryptionAvailable)
    ) {
      return SCREEN.DEFAULT_ONBOARDING;
    }

    if (
      isMobile.value &&
      width.value <= 760 &&
      route.path !== "/settings/subscription" &&
      !route.path.includes("/invitation/")
    ) {
      return SCREEN.DOWNLOAD_MOBILE_APP;
    }

    return SCREEN.APP;
  });

  return {
    screen,
    isLoaded,
  };
};
